import {
  PROXY,
  applicantSection,
  businessSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  penaltyDeclaration,
  proxyAttachments,
  replySection,
} from "modules/ebiForms/common";
import { KPA_DEADLINE } from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F4. Zezwolenie na wykonywanie zawodu przewoźnika drogowego albo licencja na krajowy transport drogowy — art. 5, 5b,
 * 5c, 7, 7a i 8 ustawy z 6.09.2001 r. o transporcie drogowym (t.j. Dz. U. 2024 poz. 1539, tekst
 * https://api.sejm.gov.pl/eli/acts/DU/2024/1539/text.pdf — pobrany 2026-10-22). Brak wzoru ogólnopolskiego; układ jak
 * wnioski Starostwa we Włocławku (zezwolenie, https://bip.wloclawski.pl/download/attachment/12876/… — pobrany) i Powiatu
 * Wyszkowskiego (licencja na przewóz osób, plik DOC — pobrany), treść pól z art. 7a ust. 2 i art. 8 ust. 2.
 * Załączniki według ustawy, nie katalogu: licencja na przewóz osób bez certyfikatu i zdolności finansowej, pośrednictwo
 * przy przewozie rzeczy bez informacji z KRK i wykazu pojazdów (art. 8 ust. 5); oświadczenie o zatrudnianiu kierowców
 * nieobowiązkowe (art. 7a ust. 3 pkt 4 uchylony). Oświadczenie o bazie eksploatacyjnej z klauzulą art. 7a ust. 6.
 * Reprezentacja przedsiębiorcy bez sekcji mocodawcy (przedsiębiorca jest w sekcji „Przedsiębiorca”) — te same kody
 * `wImieniu` / `pelnomocnictwoZwolnione`, więc działają wspólne załączniki pełnomocnictwa. Licencję na taksówkę wydaje
 * gmina, a na pośrednictwo przy przewozie osób — GITD (poza tym wnioskiem).
 */
const PERMIT: EbiCondition = { field: "rodzajUprawnienia", equals: "zezwolenie" };
const PASSENGERS: EbiCondition = { field: "rodzajUprawnienia", equals: "licencjaOsoby" };
const LICENCE: EbiCondition = { field: "rodzajUprawnienia", in: ["licencjaOsoby", "licencjaPosrednictwo"] };
const WITH_VEHICLES: EbiCondition = { field: "rodzajUprawnienia", in: ["zezwolenie", "licencjaOsoby"] };
const WITH_MANAGER: EbiCondition = { field: "rodzajUprawnienia", in: ["zezwolenie", "licencjaPosrednictwo"] };

const F4: IEbiFormDefinition = {
  code: "F4",
  version: 1,
  title: "Wniosek o zezwolenie na wykonywanie zawodu przewoźnika drogowego albo licencję na krajowy transport drogowy",
  category: EEbiFormCategory.Permits,
  office: EEbiFormOffice.County,
  description:
    "Zezwolenie na wykonywanie zawodu przewoźnika drogowego, licencja na przewóz osób samochodem osobowym lub pojazdem 7–9-osobowym albo na pośrednictwo przy przewozie rzeczy.",
  keywords: ["transport drogowy", "przewoźnik", "zezwolenie", "licencja", "przewóz osób", "pośrednictwo", "wypis"],
  organ: "Starosta (prezydent miasta na prawach powiatu) właściwy dla siedziby przedsiębiorcy",
  legalBasis:
    "art. 5, art. 5b, art. 5c, art. 7a i art. 8 ustawy z dnia 6 września 2001 r. o transporcie drogowym; rozporządzenie (WE) nr 1071/2009",
  request: "Wnoszę o udzielenie zezwolenia albo licencji wskazanej we wniosku.",
  feeLabel: "od 700 zł",
  fee: "Zezwolenie — 1000 zł oraz 110 zł za każdy wypis; licencja na przewóz osób samochodem osobowym lub pojazdem 7–9-osobowym — 700 zł, 800 zł albo 900 zł (na 2–15, 15–30 albo 30–50 lat) oraz opłaty za wypisy; licencja na pośrednictwo przy przewozie rzeczy — 800 zł, 900 zł albo 1000 zł; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline: KPA_DEADLINE,
  variantField: "rodzajUprawnienia",
  variants: {
    zezwolenie: { title: "Wniosek o udzielenie zezwolenia na wykonywanie zawodu przewoźnika drogowego" },
    licencjaOsoby: { title: "Wniosek o udzielenie licencji na krajowy transport drogowy w zakresie przewozu osób" },
    licencjaPosrednictwo: { title: "Wniosek o udzielenie licencji na pośrednictwo przy przewozie rzeczy" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj uprawnienia",
      fields: [
        {
          code: "rodzajUprawnienia",
          type: "radio",
          label: "Wnoszę o udzielenie",
          required: true,
          options: [
            { value: "zezwolenie", label: "zezwolenia na wykonywanie zawodu przewoźnika drogowego (osób lub rzeczy)" },
            {
              value: "licencjaOsoby",
              label:
                "licencji na krajowy transport drogowy w zakresie przewozu osób samochodem osobowym albo pojazdem przeznaczonym konstrukcyjnie do przewozu powyżej 7 i nie więcej niż 9 osób łącznie z kierowcą",
            },
            { value: "licencjaPosrednictwo", label: "licencji na transport drogowy w zakresie pośrednictwa przy przewozie rzeczy" },
          ],
        },
        feeInfo(
          "infoOrgan",
          "Licencję na przewóz osób taksówką wydaje wójt, burmistrz albo prezydent miasta, a licencję na pośrednictwo przy przewozie osób — Główny Inspektor Transportu Drogowego.",
        ),
      ],
    },
    applicantSection(),
    {
      code: "reprezentacja",
      title: "W czyim imieniu składasz wniosek",
      fields: [
        {
          code: "wImieniu",
          type: "radio",
          label: "Składam wniosek",
          required: true,
          defaultValue: "wlasnym",
          options: [
            { value: "wlasnym", label: "jako przedsiębiorca albo osoba uprawniona do jego reprezentacji (wpis w CEIDG albo KRS)" },
            { value: "pelnomocnik", label: "jako pełnomocnik przedsiębiorcy" },
          ],
        },
        {
          code: "pelnomocnictwoZwolnione",
          type: "checkbox",
          label:
            "Jestem małżonkiem, wstępnym, zstępnym lub rodzeństwem przedsiębiorcy — pełnomocnictwo jest zwolnione z opłaty skarbowej",
          visibleWhen: PROXY,
        },
      ],
    },
    {
      ...businessSection("Przedsiębiorca"),
      description:
        "Siedziba według KRS albo — przedsiębiorcy będącego osobą fizyczną — adres stałego miejsca wykonywania działalności gospodarczej według CEIDG.",
    },
    {
      code: "transport",
      title: "Zakres transportu drogowego",
      visibleWhen: { field: "rodzajUprawnienia", filled: true },
      fields: [
        {
          code: "rodzajPrzewozu",
          type: "checkboxes",
          label: "Rodzaj transportu drogowego",
          required: true,
          visibleWhen: PERMIT,
          options: [
            { value: "osoby", label: "przewóz osób" },
            { value: "rzeczy", label: "przewóz rzeczy" },
          ],
        },
        {
          code: "rodzajPojazduOsoby",
          type: "checkboxes",
          label: "Przewóz osób",
          required: true,
          visibleWhen: PASSENGERS,
          options: [
            { value: "osobowy", label: "samochodem osobowym" },
            { value: "do9osob", label: "pojazdem samochodowym przeznaczonym konstrukcyjnie do przewozu powyżej 7 i nie więcej niż 9 osób łącznie z kierowcą" },
          ],
        },
        {
          code: "okresLicencji",
          type: "number",
          label: "Czas, na który licencja ma być udzielona [lata]",
          required: true,
          visibleWhen: LICENCE,
          width: "third",
          min: 2,
          max: 50,
          hint: "Od 2 do 50 lat (art. 5b ust. 3); od okresu zależy opłata.",
        },
        {
          code: "pojazdy",
          type: "repeat",
          label: "Rodzaj i liczba pojazdów samochodowych, które będą wykorzystywane do wykonywania transportu drogowego",
          itemLabel: "Pojazdy",
          required: true,
          min: 1,
          max: 10,
          visibleWhen: WITH_VEHICLES,
          fields: [
            {
              code: "rodzaj",
              type: "select",
              label: "Rodzaj pojazdu",
              required: true,
              width: "twoThirds",
              options: [
                { value: "autobus", label: "autobus" },
                { value: "ciezarowy", label: "samochód ciężarowy o dopuszczalnej masie całkowitej powyżej 3,5 t" },
                { value: "ciagnik", label: "ciągnik samochodowy" },
                { value: "zespol", label: "zespół pojazdów o dopuszczalnej masie całkowitej powyżej 3,5 t" },
                { value: "osobowy", label: "samochód osobowy" },
                { value: "do9osob", label: "pojazd przeznaczony do przewozu powyżej 7 i nie więcej niż 9 osób łącznie z kierowcą" },
                { value: "inny", label: "inny pojazd samochodowy" },
              ],
            },
            { code: "liczba", type: "number", label: "Liczba", required: true, width: "third", min: 1, max: 9999 },
          ],
        },
        {
          code: "liczbaWypisow",
          type: "number",
          label: "Liczba wypisów",
          required: true,
          visibleWhen: WITH_VEHICLES,
          width: "third",
          min: 1,
          max: 9999,
          hint: "Wypis jest wydawany na każdy pojazd.",
        },
        {
          code: "oplataZezwolenie",
          type: "fee",
          label: "Opłata za udzielenie zezwolenia",
          visibleWhen: PERMIT,
          feeAmount: 1000,
        },
        {
          code: "oplataWypisy",
          type: "fee",
          label: "Opłata za wypisy z zezwolenia (110 zł za wypis)",
          visibleWhen: PERMIT,
          feeAmount: 110,
          feeUnitField: "liczbaWypisow",
        },
        {
          ...feeInfo(
            "infoOplataLicencja",
            "Opłata za licencję zależy od okresu, na który ma być udzielona: przewóz osób — 700 zł (2–15 lat), 800 zł (15–30 lat), 900 zł (30–50 lat) oraz opłaty za wypisy; pośrednictwo przy przewozie rzeczy — 800 zł, 900 zł albo 1000 zł. Urząd potwierdzi kwotę.",
          ),
          visibleWhen: LICENCE,
        },
      ],
    },
    {
      code: "zarzadzajacy",
      title: "Osoba zarządzająca transportem",
      visibleWhen: WITH_MANAGER,
      fields: [
        {
          code: "zarzadzajacyRola",
          type: "radio",
          label: "Osoba wskazana poniżej jest",
          required: true,
          visibleWhen: PERMIT,
          options: [
            { value: "zarzadzajacy", label: "zarządzającym transportem (art. 4 ust. 1 rozporządzenia (WE) nr 1071/2009)" },
            {
              value: "umowa",
              label: "osobą uprawnioną na podstawie umowy do wykonywania zadań zarządzającego transportem w imieniu przedsiębiorcy (art. 4 ust. 2)",
            },
          ],
        },
        { code: "zarzadzajacyNazwa", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
        {
          code: "zarzadzajacyCertyfikat",
          type: "text",
          label: "Numer certyfikatu kompetencji zawodowych",
          required: true,
          width: "half",
          maxLength: 50,
        },
        { code: "zarzadzajacyAdres", type: "text", label: "Adres zamieszkania", required: true, visibleWhen: PERMIT, maxLength: 300 },
      ],
    },
    {
      code: "baza",
      title: "Siedziba i baza eksploatacyjna",
      visibleWhen: PERMIT,
      fields: [
        {
          code: "lokaleDokumentow",
          type: "text",
          label: "Adres lokalu, w którym są przechowywane dokumenty przedsiębiorstwa, jeżeli jest inny niż siedziba",
          maxLength: 300,
          hint: "Art. 5 ust. 1 lit. a rozporządzenia (WE) nr 1071/2009.",
        },
        { code: "bazaAdres", type: "text", label: "Adres bazy eksploatacyjnej, jeżeli jest inny niż siedziba", maxLength: 300 },
        {
          code: "bazaOswiadczenie",
          type: "declaration",
          label: "Oświadczam, że przedsiębiorca dysponuje bazą eksploatacyjną (art. 7a ust. 3 pkt 3 ustawy o transporcie drogowym).",
          required: true,
        },
        penaltyDeclaration("bazaKlauzula", "Jestem świadomy/a odpowiedzialności karnej za złożenie fałszywego oświadczenia."),
      ],
    },
    replySection("Sposób odbioru decyzji"),
  ],
  attachments: [
    {
      code: "oswiadczenieZarzadzajacego",
      label: "Oświadczenie osoby zarządzającej transportem (albo osoby uprawnionej na podstawie umowy)",
      hint: "Podpisane przez tę osobę, o treści z art. 7a ust. 3 pkt 1 (zezwolenie) albo art. 8 ust. 3 pkt 1 (licencja) ustawy o transporcie drogowym.",
      visibleWhen: WITH_MANAGER,
      required: true,
    },
    { code: "certyfikat", label: "Kopia certyfikatu kompetencji zawodowych", visibleWhen: WITH_MANAGER, required: true },
    {
      code: "zdolnoscFinansowa",
      label: "Dokumenty potwierdzające zdolność finansową",
      hint: "Zezwolenie — art. 7 rozporządzenia (WE) nr 1071/2009 (np. roczne sprawozdanie finansowe, gwarancja bankowa, ubezpieczenie); pośrednictwo przy przewozie rzeczy — środki lub majątek o wartości 50 000 euro (art. 5c ust. 2 pkt 2 i ust. 3).",
      multiple: true,
      visibleWhen: WITH_MANAGER,
      required: true,
    },
    {
      code: "krk",
      label: "Informacja z Krajowego Rejestru Karnego (zaświadczenie o niekaralności)",
      hint: "Zezwolenie — dotycząca członków organu zarządzającego albo przedsiębiorcy i zarządzającego transportem (art. 7a ust. 3 pkt 6); licencja na przewóz osób — przedsiębiorcy wykonującego przewozy i kierowców, z datą nie wcześniejszą niż miesiąc przed złożeniem wniosku (art. 8 ust. 3 pkt 4).",
      multiple: true,
      visibleWhen: WITH_VEHICLES,
      required: true,
    },
    {
      code: "oswiadczeniaNiekaralnosc",
      label: "Oświadczenia o niekaralności za poważne naruszenia i o nieskazaniu za granicą (art. 7a ust. 3 pkt 7 i 8)",
      hint: "Podpisane przez osoby, których dotyczy informacja z KRK, z klauzulą „Jestem świadomy/a odpowiedzialności karnej za złożenie fałszywego oświadczenia.” (art. 7a ust. 6).",
      multiple: true,
      visibleWhen: PERMIT,
      required: true,
    },
    {
      code: "dobraReputacja",
      label: "Oświadczenia o spełnianiu wymogu dobrej reputacji (art. 8 ust. 3 pkt 2)",
      hint: "Członków organu zarządzającego, osób zarządzających spółką albo osoby prowadzącej działalność — z klauzulą „Jestem świadomy/a odpowiedzialności karnej za złożenie fałszywego oświadczenia.” (art. 8 ust. 6).",
      multiple: true,
      visibleWhen: LICENCE,
      required: true,
    },
    {
      code: "dokumentyKierowcow",
      label: "Dokumenty kierowców potwierdzające wymagania z art. 39a ust. 1 pkt 2–4",
      hint: "Kopie praw jazdy oraz orzeczeń lekarskich i psychologicznych (art. 8 ust. 3 pkt 3a).",
      multiple: true,
      visibleWhen: PASSENGERS,
      required: true,
    },
    {
      code: "wykazPojazdow",
      label: "Wykaz pojazdów",
      hint: "Marka, typ, rodzaj/przeznaczenie, numer rejestracyjny, VIN i tytuł prawny do dysponowania pojazdem. Przy zezwoleniu wykaz można złożyć po jego uzyskaniu (art. 7a ust. 7).",
      visibleWhen: WITH_VEHICLES,
      requiredWhen: PASSENGERS,
    },
    {
      code: "oswiadczenieKierowcy",
      label: "Oświadczenie o zatrudnianiu kierowców lub współpracy z osobami wykonującymi osobiście przewozy",
      hint: "(praktyka urzędów) — nieobowiązkowo; przepis art. 7a ust. 3 pkt 4 został uchylony.",
      visibleWhen: PERMIT,
    },
    ...proxyAttachments(),
    feeProofAttachment("Dowód uiszczenia opłaty za udzielenie zezwolenia (licencji) i wypisów", {
      visibleWhen: { field: "rodzajUprawnienia", filled: true },
    }),
    otherAttachments(),
  ],
};

export default F4;
